import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDatepickerMaskDirective } from '@anihis/shared/src/shared/directive/mat-datepicker-mask.directive';
import { MatRadioModule } from '@angular/material/radio';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule } from '@jsverse/transloco';
import { Subject, combineLatest } from 'rxjs';
import { debounceTime, takeUntil, startWith } from 'rxjs/operators';
import {
  AnimalDto,
  SerialNumberConfirmationDto,
  SerialNumberConfirmationsService,
  MedicinesService,
  MedicineDto,
  HealthRecordItemsService,
  ServiceDto,
} from '@anihis/core/data-access';
import { CountryPickerComponent } from '@anihis/shared/src/shared/component/country-picker/country-picker.component';
import { CountryItem } from '@anihis/shared/src/countries/countries';
import { AuthenticationService } from '@anihis/shared/src/services/authentication.service';
import { SelectClinicStore } from '../../../../../../shared/component/select-clinic/select-clinic.store';
import {
  getMaxBirthDate,
  getMinAnimalBirthDate,
} from '@anihis/shared/src/validators/birth-date.validators';

@Component({
  selector: 'anihis-rabies-vaccination-certificate',
  templateUrl: './rabies-vaccination-certificate.component.html',
  styleUrls: ['./rabies-vaccination-certificate.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatDatepickerMaskDirective,
    MatRadioModule,
    MatCheckboxModule,
    MatIconModule,
    TranslocoModule,
    CountryPickerComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RabiesVaccinationCertificateComponent
  implements OnInit, OnChanges, OnDestroy
{
  minDate = getMinAnimalBirthDate();
  today = getMaxBirthDate();

  @Input() animalData: AnimalDto | null = null;
  @Output() formChange = new EventEmitter<any>();

  form: FormGroup;
  availableSerialNumbers: SerialNumberConfirmationDto[] = [];
  vaccines: MedicineDto[] = [];
  selectedSerialNumberControl = new FormControl<string | null>(null);
  selectedCountryCode: string | null = null;

  readonly SUPPORTED_COUNTRY_CODE = 'RS';
  private readonly MARKING_FEE_DOG = 'Fee for marking dog';
  private readonly MARKING_FEE_CAT = 'Fee for marking cat';
  private readonly RABIES_VACCINE_TYPE_NAMES = new Set([
    'besnilo',
    'rabies',
    'vakcine',
    'vaccines',
  ]);

  private destroy$ = new Subject<void>();
  private markingFees = new Map<string, ServiceDto>();
  private serialNumberConfirmationsService = inject(
    SerialNumberConfirmationsService,
  );
  private medicinesService = inject(MedicinesService);
  private healthRecordItemsService = inject(HealthRecordItemsService);
  private authenticationService = inject(AuthenticationService);
  private selectClinicStore = inject(SelectClinicStore);
  private cd = inject(ChangeDetectorRef);

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      serialNumberConfirmationUid: [{ value: '', disabled: true }],
      vaccineSerialNumber: [''],
      // checkboxes: marking and vaccination (both may be selected). Keep markingSelected for server-side PDF template.
      marking: [false],
      vaccination: [false],
      vaccinationSelected: [false],
      markingSelected: [false],
      passportNumber: [''],
      microchip: [''],
      dateIssued: [new Date()],
      vaccineName: [''],
      markingAndRegistrationFee: [null, [Validators.min(0)]],
      vaccinationFee: [null, [Validators.min(0)]],
      totalAmount: [{ value: null, disabled: true }],
      veterinarianName: [''],
      ownerJmbg: [''],
    });
  }

  ngOnInit(): void {
    this.loadSerialNumbers();
    this.loadVaccines();
    this.loadMarkingFees();

    // If animal already has a microchip, mark 'marking' as true and disable it;
    // set microchip control value and disable input. Otherwise ensure marking is enabled.
    if (this.animalData?.microchip) {
      this.form.get('microchip')?.setValue(this.animalData.microchip, {
        emitEvent: false,
      });
      this.form.get('microchip')?.disable({ emitEvent: false });
      this.form.get('marking')?.setValue(true, { emitEvent: false });
      this.form.get('marking')?.disable({ emitEvent: false });
    } else {
      this.form.get('microchip')?.enable({ emitEvent: false });
      this.form.get('marking')?.enable({ emitEvent: false });
    }

    this.selectedSerialNumberControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((uid) => {
        if (uid) {
          const item = this.availableSerialNumbers.find((s) => s.uid === uid);
          if (item) {
            this.form.patchValue(
              {
                serialNumberConfirmationUid: uid,
              },
              { emitEvent: false },
            );
          }
        } else {
          this.form.patchValue(
            { serialNumberConfirmationUid: '' },
            { emitEvent: false },
          );
        }
        this.formChange.emit(this.form.getRawValue());
      });

    // When marking checkbox changes, enable/disable passport/microchip inputs and emit form change
    this.form
      .get('marking')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((isMarking) => {
        this.form
          .get('markingSelected')
          ?.setValue(isMarking, { emitEvent: false });
        if (isMarking) {
          this.form.get('passportNumber')?.enable({ emitEvent: false });
          this.form.get('microchip')?.enable({ emitEvent: false });
        } else {
          this.form.get('passportNumber')?.disable({ emitEvent: false });
          // If not marking, reset passport and microchip to empty so saved JSON doesn't contain them
          this.form.get('passportNumber')?.setValue('', { emitEvent: false });
          this.form.get('microchip')?.setValue('', { emitEvent: false });
          this.form.get('microchip')?.disable({ emitEvent: false });
        }
        this.formChange.emit(this.form.getRawValue());
        this.cd.markForCheck();
      });

    // Keep vaccinationSelected in sync so server-side template can use it
    this.form
      .get('vaccination')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((isVaccination) => {
        this.form
          .get('vaccinationSelected')
          ?.setValue(isVaccination, { emitEvent: false });
        this.formChange.emit(this.form.getRawValue());
        this.cd.markForCheck();
      });

    this.vaccineName.valueChanges
      .pipe(
        startWith(this.vaccineName.value),
        takeUntil(this.destroy$),
      )
      .subscribe((name) => this.applyVaccinePrice(name));

    combineLatest([
      this.markingAndRegistrationFee.valueChanges.pipe(
        startWith(this.markingAndRegistrationFee.value),
      ),
      this.vaccinationFee.valueChanges.pipe(
        startWith(this.vaccinationFee.value),
      ),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([fee1, fee2]) => {
        const n1 = Number(fee1);
        const n2 = Number(fee2);
        const a = Number.isFinite(n1) ? n1 : 0;
        const b = Number.isFinite(n2) ? n2 : 0;
        const total = a + b;
        this.totalAmount.setValue(total !== 0 ? total : null, {
          emitEvent: false,
        });
      });

    this.form.valueChanges
      .pipe(debounceTime(250), takeUntil(this.destroy$))
      .subscribe(() => this.formChange.emit(this.form.getRawValue()));

    // Note: passport and microchip updates are performed on Save in the parent dialog.
  }

  private getClinicUid(): string | undefined {
    const role = this.authenticationService.getRoleFromToken();
    if (role === 'Clinic') {
      return this.authenticationService.getDomainUid() ?? undefined;
    }
    if (role === 'ClinicOwner') {
      return this.selectClinicStore.selectedClinicUid() ?? undefined;
    }
    return undefined;
  }

  private loadMarkingFees(): void {
    this.healthRecordItemsService
      .healthRecordItemsServicesGet(
        undefined,
        'Fee for marking',
        false,
        this.getClinicUid(),
        1,
        100,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          const items = (result.services ?? []) as ServiceDto[];
          for (const service of items) {
            if (
              service.name === this.MARKING_FEE_DOG ||
              service.name === this.MARKING_FEE_CAT
            ) {
              this.markingFees.set(service.name, service);
            }
          }
          this.applyMarkingFee();
          this.cd.markForCheck();
        },
      });
  }

  private applyMarkingFee(): void {
    const speciesName = this.animalData?.species?.name?.toLowerCase();
    let serviceName: string | null = null;
    if (speciesName === 'dog') {
      serviceName = this.MARKING_FEE_DOG;
    } else if (speciesName === 'cat') {
      serviceName = this.MARKING_FEE_CAT;
    }

    if (!serviceName) {
      this.markingAndRegistrationFee.setValue(null, { emitEvent: true });
      this.formChange.emit(this.form.getRawValue());
      return;
    }

    const service = this.markingFees.get(serviceName);
    const price =
      service?.price != null && Number.isFinite(service.price)
        ? service.price
        : null;
    this.markingAndRegistrationFee.setValue(price, { emitEvent: true });
    this.formChange.emit(this.form.getRawValue());
    this.cd.markForCheck();
  }

  private applyVaccinePrice(vaccineName: string | null): void {
    const vaccine = this.vaccines.find((v) => v.name === vaccineName);
    if (vaccine?.name) {
      const price =
        vaccine.price != null && Number.isFinite(vaccine.price)
          ? vaccine.price
          : null;
      this.vaccinationFee.setValue(price, { emitEvent: true });
      this.vaccinationFee.disable({ emitEvent: true });
    } else {
      this.vaccinationFee.enable({ emitEvent: true });
      this.vaccinationFee.setValue(null, { emitEvent: true });
    }
    this.formChange.emit(this.form.getRawValue());
    this.cd.markForCheck();
  }

  private loadVaccines(): void {
    this.medicinesService
      .medicinesGet(
        undefined,
        this.getClinicUid(),
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        1,
        500,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          const items = (result.medicines ?? []) as MedicineDto[];
          this.vaccines = items.filter((m) =>
            this.isRabiesVaccineMedicineType(m.type?.name),
          );
          if (this.vaccineName.value) {
            this.applyVaccinePrice(this.vaccineName.value);
          }
          this.cd.markForCheck();
        },
      });
  }

  private isRabiesVaccineMedicineType(typeName?: string | null): boolean {
    const normalized = (typeName ?? '').trim().toLowerCase();
    return normalized.length > 0 && this.RABIES_VACCINE_TYPE_NAMES.has(normalized);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['animalData']) {
      const a = changes['animalData'].currentValue as AnimalDto | null;
      if (a) {
        if (a.microchip) {
          this.form.get('microchip')?.setValue(a.microchip, {
            emitEvent: false,
          });
          this.form.get('microchip')?.disable({ emitEvent: false });
          this.form.get('marking')?.setValue(true, { emitEvent: false });
          this.form.get('marking')?.disable({ emitEvent: false });
        } else {
          this.form.get('microchip')?.setValue('', { emitEvent: false });
          this.form.get('microchip')?.enable({ emitEvent: false });
          this.form.get('marking')?.enable({ emitEvent: false });
        }
        // Prefill passport/microchip if present on the animal
        if (a.passportNumber) {
          this.form
            .get('passportNumber')
            ?.setValue(a.passportNumber, { emitEvent: false });
        }
        if (a.microchip) {
          this.form
            .get('microchip')
            ?.setValue(a.microchip, { emitEvent: false });
        }
        this.applyMarkingFee();
        this.cd.markForCheck();
      }
    }
  }

  private loadSerialNumbers(): void {
    this.serialNumberConfirmationsService
      .serialNumberConfirmationsGet(undefined, 1, 500)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.availableSerialNumbers = (result.items ?? []).filter(
            (s) => !s.used,
          );
          this.cd.markForCheck();
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get serialNumberConfirmationUid(): FormControl {
    return this.form.get('serialNumberConfirmationUid') as FormControl;
  }

  get vaccineSerialNumber(): FormControl {
    return this.form.get('vaccineSerialNumber') as FormControl;
  }

  get dateIssued(): FormControl {
    return this.form.get('dateIssued') as FormControl;
  }

  get vaccineName(): FormControl {
    return this.form.get('vaccineName') as FormControl;
  }

  get markingAndRegistrationFee(): FormControl {
    return this.form.get('markingAndRegistrationFee') as FormControl;
  }

  get vaccinationFee(): FormControl {
    return this.form.get('vaccinationFee') as FormControl;
  }

  get totalAmount(): FormControl {
    return this.form.get('totalAmount') as FormControl;
  }

  get veterinarianName(): FormControl {
    return this.form.get('veterinarianName') as FormControl;
  }

  get ownerJmbg(): FormControl {
    return this.form.get('ownerJmbg') as FormControl;
  }
  get passportNumber(): FormControl {
    return this.form.get('passportNumber') as FormControl;
  }

  get microchip(): FormControl {
    return this.form.get('microchip') as FormControl;
  }

  get markingSelectedControl(): FormControl {
    return this.form.get('markingSelected') as FormControl;
  }

  // New getters for marking/vaccination
  get marking(): FormControl {
    return this.form.get('marking') as FormControl;
  }

  get vaccination(): FormControl {
    return this.form.get('vaccination') as FormControl;
  }

  /** True when both microchip and passport already exist on the animal — hides marking checkbox */
  get bothAlreadyExist(): boolean {
    return !!(this.animalData?.microchip && this.animalData?.passportNumber);
  }

  onCountrySelected(item: CountryItem | null): void {
    this.selectedCountryCode = item?.code ?? null;
    this.cd.markForCheck();
  }

  clearForm() {
    this.selectedCountryCode = null;
    this.selectedSerialNumberControl.setValue(null, { emitEvent: false });
    // Ensure controls are enabled before resetting so form is interactive next time
    this.form.get('marking')?.enable({ emitEvent: false });
    this.form.get('microchip')?.enable({ emitEvent: false });

    this.form.reset({
      serialNumberConfirmationUid: '',
      vaccineSerialNumber: '',
      marking: false,
      vaccination: false,
      markingSelected: false,
      vaccinationSelected: false,
      passportNumber: '',
      microchip: '',
      dateIssued: new Date(),
      vaccineName: '',
      markingAndRegistrationFee: null,
      vaccinationFee: null,
      totalAmount: null,
      veterinarianName: '',
      ownerJmbg: '',
    });
    this.vaccinationFee.enable({ emitEvent: false });
    this.applyMarkingFee();
  }
}
